import React from 'react';
import { useNavigate } from 'react-router-dom';
import Joyride, { CallBackProps, STATUS, Step, TooltipRenderProps } from 'react-joyride';
import styled, { keyframes, css } from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { Settings, Building2, Plus, Search, Crown, HelpCircle, ArrowRight, X, XCircle } from 'lucide-react';
import { useTutorialStore, TUTORIAL_ROUTES } from '@/store/useTutorialStore';

interface SpaceManagementTutorialProps {
  run: boolean;
  onComplete: () => void;
}

/**
 * 회사 관리 튜토리얼 스텝 정의
 */
const steps: Step[] = [
  {
    target: 'body',
    content: 'intro',
    placement: 'center',
    disableBeacon: true,
    disableOverlay: false,
  },
  {
    target: '[data-tutorial="action-section"]',
    content: 'actions',
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tutorial="company-list"]',
    content: 'company-list',
    placement: 'top',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tutorial="help-section"]',
    content: 'help',
    placement: 'left',
    disableBeacon: true,
    spotlightPadding: 8,
  },
];

/**
 * 스텝별 컨텐츠 정의
 */
const stepContents: Record<string, { icon: React.ReactNode; title: string; description: string }> = {
  intro: {
    icon: <Settings size={32} />,
    title: '회사 관리',
    description: '회사 관리 페이지에 오신 것을 환영합니다!\n\n이곳에서 새 회사를 등록하고 마스터로서 회사를 관리할 수 있습니다.',
  },
  actions: {
    icon: <Plus size={28} />,
    title: '빠른 액션',
    description: '이곳에서 빠르게 작업을 시작할 수 있습니다.\n\n• 새 회사 등록: 새로운 회사를 생성하고 마스터가 됩니다\n• 회사 찾기: 기존 회사에 참여하여 협업을 시작합니다',
  },
  'company-list': {
    icon: <Crown size={28} />,
    title: '관리 중인 회사',
    description: '마스터 권한이 있는 회사 목록입니다.\n\n각 회사 카드를 클릭하면:\n• 스페이스 추가/삭제\n• 팀원 초대\n• 회사 정보 수정\n등의 관리 작업을 할 수 있습니다.',
  },
  help: {
    icon: <HelpCircle size={28} />,
    title: '도움말',
    description: '사용법이 헷갈리신다면 도움말을 참고하세요.\n\n주요 기능과 사용 방법이 정리되어 있습니다.',
  },
};

/**
 * 커스텀 툴팁 컴포넌트
 */
const CustomTooltip: React.FC<TooltipRenderProps> = ({
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  tooltipProps,
  isLastStep,
  size,
}) => {
  const navigate = useNavigate();
  const { completeTutorialAndGoNext, completeTutorial } = useTutorialStore();
  const content = stepContents[step.content as string];
  const isIntro = step.content === 'intro';
  
  // localStorage에서 온보딩 플로우 여부 직접 확인
  const isOnboarding = localStorage.getItem('factsheet_onboarding_flow') === 'true';

  // 다음 페이지로 이동
  const handleNavigateClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    closeProps.onClick(e);
    const nextPage = completeTutorialAndGoNext();
    if (nextPage) {
      navigate(TUTORIAL_ROUTES[nextPage] + '?tutorial=true');
    }
  };

  // 튜토리얼 닫기 (완료 처리)
  const handleCloseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    closeProps.onClick(e);
    completeTutorial('space-management');
  };

  return (
    <TooltipContainer {...tooltipProps} $isIntro={isIntro}>
      <CloseButton {...closeProps}>
        <X size={18} />
      </CloseButton>
      
      <TooltipHeader $isIntro={isIntro}>
        <IconWrapper $isIntro={isIntro} $color="#059669">
          {content.icon}
        </IconWrapper>
        <TooltipTitle>{content.title}</TooltipTitle>
      </TooltipHeader>
      
      <TooltipContent>
        {content.description.split('\n').map((line, i) => (
          <p key={i}>{line || <br />}</p>
        ))}
      </TooltipContent>
      
      <TooltipFooter>
        <StepIndicator>
          {Array.from({ length: size }, (_, i) => (
            <StepDot key={i} $active={i === index} $color="#059669" />
          ))}
        </StepIndicator>
        
        <ButtonGroup>
          {index > 0 && (
            <BackButton {...backProps}>
              이전
            </BackButton>
          )}
          {isLastStep ? (
            <>
              <CloseButtonStyled onClick={handleCloseClick}>
                <XCircle size={16} /> 닫기
              </CloseButtonStyled>
              {isOnboarding && (
                <PrimaryButton onClick={handleNavigateClick} $color="#059669">
                  계정 설정으로 이동 <ArrowRight size={16} />
                </PrimaryButton>
              )}
            </>
          ) : (
            <PrimaryButton {...primaryProps} $color="#059669">
              다음 <ArrowRight size={16} />
            </PrimaryButton>
          )}
        </ButtonGroup>
      </TooltipFooter>
    </TooltipContainer>
  );
};

/**
 * 회사 관리 튜토리얼 컴포넌트
 */
const SpaceManagementTutorial: React.FC<SpaceManagementTutorialProps> = ({ run, onComplete }) => {
  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    
    if (finishedStatuses.includes(status)) {
      onComplete();
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress={false}
      showSkipButton
      scrollToFirstStep
      disableOverlayClose={false}
      spotlightClicks
      spotlightPadding={12}
      callback={handleJoyrideCallback}
      tooltipComponent={CustomTooltip}
      floaterProps={{
        styles: {
          floater: {
            filter: 'drop-shadow(0 10px 40px rgba(0, 0, 0, 0.2))',
          },
        },
      }}
      styles={{
        options: {
          arrowColor: '#fff',
          backgroundColor: '#fff',
          overlayColor: 'rgba(0, 0, 0, 0.6)',
          primaryColor: '#059669',
          zIndex: 10000,
        },
        spotlight: {
          borderRadius: 16,
        },
      }}
    />
  );
};

// ============================================
// Animations
// ============================================

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// ============================================
// Styled Components
// ============================================

const TooltipContainer = styled.div<{ $isIntro?: boolean }>`
  position: relative;
  background: #fff;
  border-radius: 20px;
  padding: 24px;
  min-width: ${props => props.$isIntro ? '420px' : '340px'};
  max-width: 440px;
  animation: ${fadeIn} 0.3s ease-out;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  
  ${props => props.$isIntro && `
    text-align: center;
    padding: 32px;
  `}
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: ${semanticColors.background.tertiary};
  color: ${semanticColors.text.secondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.secondary};
    color: ${semanticColors.text.primary};
  }
`;

const TooltipHeader = styled.div<{ $isIntro?: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
  
  ${props => props.$isIntro && `
    flex-direction: column;
    gap: 16px;
    margin-bottom: 20px;
  `}
`;

const IconWrapper = styled.div<{ $isIntro?: boolean; $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props => props.$isIntro ? '72px' : '52px'};
  height: ${props => props.$isIntro ? '72px' : '52px'};
  border-radius: 16px;
  background: linear-gradient(135deg, ${props => props.$color} 0%, ${props => props.$color}dd 100%);
  color: #fff;
  flex-shrink: 0;
  
  ${props => props.$isIntro && css`
    animation: ${pulse} 2s ease-in-out infinite;
  `}
`;

const TooltipTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const TooltipContent = styled.div`
  font-size: 0.9375rem;
  line-height: 1.7;
  color: ${semanticColors.text.secondary};
  margin-bottom: 20px;
  
  p {
    margin: 0;
  }
`;

const TooltipFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  border-top: 1px solid ${semanticColors.border.primary};
`;

const StepIndicator = styled.div`
  display: flex;
  gap: 6px;
`;

const StepDot = styled.div<{ $active: boolean; $color: string }>`
  width: ${props => props.$active ? '20px' : '8px'};
  height: 8px;
  border-radius: 100px;
  background: ${props => props.$active ? props.$color : semanticColors.background.tertiary};
  transition: all 0.3s ease;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const BackButton = styled.button`
  padding: 10px 18px;
  border-radius: 10px;
  border: 1px solid ${semanticColors.border.primary};
  background: #fff;
  color: ${semanticColors.text.secondary};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.tertiary};
    border-color: ${semanticColors.border.secondary};
  }
`;

const CloseButtonStyled = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 10px;
  border: 1px solid ${semanticColors.border.primary};
  background: #fff;
  color: ${semanticColors.text.secondary};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.tertiary};
    border-color: ${semanticColors.border.secondary};
    color: ${semanticColors.text.primary};
  }
`;

const PrimaryButton = styled.button<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, ${props => props.$color} 0%, ${props => props.$color}dd 100%);
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${props => props.$color}66;
  }
`;

export default SpaceManagementTutorial;

